import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, Check, MessageSquare } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { GA } from "@/components/ads/GoogleConnect";
import { CampaignWizard } from "@/components/ads/CampaignWizard";

const LABEL = {
  set_budget: (a) => `Buget zilnic „${a.campaign_name}” → ${a.daily_ron} lei/zi`,
  pause_campaign: (a) => `Pune pe pauză „${a.campaign_name}”`,
  enable_campaign: (a) => `Pornește „${a.campaign_name}”`,
  add_negative: (a) => `Cuvânt negativ „${a.text}” în „${a.campaign_name}”`,
  create_campaign: (a) => `Campanie nouă ${a.campaign_type === "PMAX" ? "Performance Max" : "Search"}${a.theme ? ` — ${a.theme}` : ""}`,
};

export const AdsChat = ({ base = GA, platform = "Google Ads", labels = LABEL, Wizard = CampaignWizard, tid = "ads-chat" }) => {
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState("");
  const [wiz, setWiz] = useState(null);
  const box = useRef(null);
  useEffect(() => { axios.get(`${base}/chat`).then((r) => setItems(r.data.items || [])).catch(() => {}); }, []);
  useEffect(() => { box.current?.scrollTo({ top: box.current.scrollHeight, behavior: "smooth" }); }, [items.length, busy]);

  const send = async () => {
    const t = text.trim();
    if (!t || busy) return;
    setBusy("send"); setText("");
    setItems((x) => [...x, { id: "tmp", role: "user", text: t }]);
    try { const r = await axios.post(`${base}/chat`, { text: t }, { timeout: 150000 }); setItems((x) => [...x.filter((m) => m.id !== "tmp"), ...r.data.items]); }
    catch (e) { setItems((x) => x.filter((m) => m.id !== "tmp")); setText(t); toast.error(e.response?.data?.detail || e.message); }
    setBusy("");
  };
  const apply = async (m, i) => {
    const a = m.actions[i];
    if (a.type === "create_campaign") return setWiz(a);
    if (!window.confirm(`${labels[a.type](a)}?\n\nSe modifică direct în ${platform}.`)) return;
    setBusy(`${m.id}-${i}`);
    try {
      await axios.post(`${base}/chat/${m.id}/${i}/apply`);
      setItems((x) => x.map((y) => (y.id === m.id ? { ...y, actions: y.actions.map((z, j) => (j === i ? { ...z, status: "applied" } : z)) } : y)));
      toast.success(`Aplicat în ${platform}`);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setBusy("");
  };

  return (
    <div className="rounded-xl border border-amber-400/30 bg-zinc-900/60" data-testid={tid}>
      <p className="flex items-center gap-2 border-b border-zinc-800 px-4 py-2.5 text-sm font-semibold text-zinc-100"><MessageSquare className="h-4 w-4 text-amber-400" /> Chat {platform} <span className="text-xs font-normal text-zinc-500">— conversația rămâne salvată aici</span></p>
      <div ref={box} className="max-h-[420px] min-h-[160px] space-y-3 overflow-y-auto p-4" data-testid="ads-chat-messages">
        {!items.length && <p className="text-xs text-zinc-500">Întreabă orice despre {platform} sau spune ce vrei schimbat, ex. „scade bugetul la 70 lei”, „de ce am ROAS mic?”, „fă-mi o campanie nouă pentru espressor”. Modificările se aplică doar când apeși „Aplică”.</p>}
        {items.map((m) => (
          <div key={m.id} className={m.role === "user" ? "flex justify-end" : ""} data-testid={`ads-chat-msg-${m.id}`}>
            <div className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3.5 py-2 text-sm ${m.role === "user" ? "bg-amber-400 text-zinc-950" : "bg-zinc-800 text-zinc-100"}`}>
              {m.text}
              {(m.actions || []).map((a, i) => (
                <div key={i} className="mt-2 flex items-center gap-2 rounded-lg border border-zinc-700 bg-zinc-900 px-2.5 py-1.5 text-xs">
                  <span className="flex-1 text-amber-200">{labels[a.type]?.(a)}</span>
                  {a.status === "applied" ? <span className="text-emerald-400" data-testid={`ads-chat-applied-${m.id}-${i}`}>aplicat ✓</span> : (
                    <button data-testid={`ads-chat-apply-${m.id}-${i}`} onClick={() => apply(m, i)} disabled={!!busy} className="flex items-center gap-1 rounded-full bg-emerald-500 px-2.5 py-0.5 font-semibold text-zinc-950">
                      {busy === `${m.id}-${i}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} {a.type === "create_campaign" ? "Creează" : "Aplică"}</button>)}
                </div>))}
            </div>
          </div>))}
        {busy === "send" && <Loader2 className="h-4 w-4 animate-spin text-zinc-500" data-testid="ads-chat-thinking" />}
      </div>
      <div className="flex gap-2 border-t border-zinc-800 p-3">
        <Textarea data-testid="ads-chat-input" value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder={`Scrie despre ${platform}…`} className="text-sm"
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} />
        <button data-testid="ads-chat-send" onClick={send} disabled={!!busy || !text.trim()} className="flex w-12 items-center justify-center rounded-lg bg-amber-400 text-zinc-950 disabled:opacity-50">
          {busy === "send" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</button>
      </div>
      {wiz && <Wizard init={wiz} onClose={() => setWiz(null)} />}
    </div>
  );
};
